import type { ProviderAccount } from '@/api/schemas';
import { formatDateTime, timeAgo } from '@/lib/format';
import { useNow } from '@/lib/useNow';
import { CompactTable, type CompactColumn } from '@/components/CompactTable';
import { Icon } from '@/components/Icon';
import { StatusPill, Tag } from '@/components/badges';
import { ProviderBadge } from '@/components/ProviderBadge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { accountHealth, managedByLook, type HealthLook } from './providerAccountModel';
import type { ProviderAccountActions } from './useProviderAccountActions';

// Kept here for the callers that import it from the table.
export { accountHealth };

function TokenCell({ account }: { account: ProviderAccount }) {
  if (account.credentialId) {
    return (
      <span className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground">
        <Icon name="key" size={14} className="text-succeeded" />
        stored
      </span>
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="inline-flex items-center gap-1 rounded-sm font-mono text-xs text-subtle">
          <Icon name="key_off" size={14} />
          worker env
        </span>
      </TooltipTrigger>
      <TooltipContent>No token stored in Hangar: the worker uses its HANGAR_EAS_TOKEN</TooltipContent>
    </Tooltip>
  );
}

function LastCheck({ account, now }: { account: ProviderAccount; now: number }) {
  const checkedAt = account.health?.checkedAt;
  return checkedAt ? <span title={formatDateTime(checkedAt)}>{timeAgo(checkedAt, now)}</span> : <span className="text-subtle">never</span>;
}

function AccountCell({ a }: { a: ProviderAccount }) {
  return (
    <span className="flex min-w-0 flex-col">
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate text-[13px] font-medium text-foreground" title={a.name}>
          {a.name}
        </span>
        <Tag title={managedByLook(a).title} className="shrink-0 px-1 py-0 text-[10px]">
          {managedByLook(a).short}
        </Tag>
      </span>
      <span className="flex min-w-0 items-center gap-1.5 font-mono text-xs text-subtle">
        <ProviderBadge kind={a.kind} className="px-1 py-0 text-[10px] leading-4" />
        <span className="min-w-0 truncate">
          {a.externalAccount ?? '—'}
          {/* Narrow cards fold the token column into this line. */}
          <span className="hidden @max-[30rem]:inline"> · {a.credentialId ? 'token stored' : 'worker env'}</span>
        </span>
      </span>
    </span>
  );
}

function SlotsCell({ a }: { a: ProviderAccount }) {
  return (
    <span className="tabular font-mono text-xs text-muted-foreground" title={`${a.inUse} in use of ${a.concurrencyLimit}`}>
      {a.inUse}/{a.concurrencyLimit}
    </span>
  );
}

function PhoneRow({ a, health, now }: { a: ProviderAccount; health: HealthLook; now: number }) {
  return (
    <span className="flex min-w-0 flex-col gap-1">
      <span className="flex min-w-0 items-center gap-2">
        <span className="truncate text-sm font-medium text-foreground">{a.name}</span>
        <StatusPill {...health} />
        <Tag title={managedByLook(a).title}>{managedByLook(a).short}</Tag>
      </span>
      <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-xs text-subtle">
        <ProviderBadge kind={a.kind} />
        <span>{a.externalAccount ?? '—'}</span>
        <span>
          {a.inUse}/{a.concurrencyLimit} slots
        </span>
        <TokenCell account={a} />
        <span>
          checked <LastCheck account={a} now={now} />
        </span>
      </span>
    </span>
  );
}

/**
 * Step 1's table of build provider accounts (Expo EAS and Bitrise): name and provider, token, slots, last check and
 * status, with the row menu for platform operators (client admins read it only). A Bitrise account before the API builds on Bitrise (`bitrise: false`) says "Builds soon".
 */
export function ProviderAccountsTable({ accounts, actions, bitrise }: { accounts: ProviderAccount[]; actions: ProviderAccountActions; bitrise: boolean }) {
  const now = useNow(30_000);
  const health = (a: ProviderAccount) => accountHealth(a, actions.testing === a.id, a.kind !== 'bitrise' || bitrise);
  const columns: CompactColumn<ProviderAccount>[] = [
    { key: 'account', header: 'Account', cell: (a) => <AccountCell a={a} />, className: 'max-w-0', width: 'w-[40%]' },
    { key: 'token', header: 'Token', cell: (a) => <TokenCell account={a} />, hide: '@max-[30rem]:hidden' },
    { key: 'slots', header: 'Slots', cell: (a) => <SlotsCell a={a} /> },
    { key: 'check', header: 'Last check', cell: (a) => <span className="font-mono text-xs text-muted-foreground"><LastCheck account={a} now={now} /></span>, hide: '@max-[34rem]:hidden' },
    { key: 'status', header: 'Status', cell: (a) => <StatusPill {...health(a)} /> },
  ];
  return (
    <CompactTable
      label="Build providers"
      rows={accounts}
      columns={columns}
      rowKey={(a) => a.id}
      rowTestId={(a) => `gs-account-${a.externalAccount ?? a.id}`}
      subject={(a) => a.name}
      actions={actions.operator ? actions.actionsFor : undefined}
      actionsVariant="menu"
      phoneRow={(a) => <PhoneRow a={a} health={health(a)} now={now} />}
    />
  );
}
